/* eslint-disable sonarjs/cognitive-complexity */
import axios from 'axios';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useContext, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DEAL_LOCATION_TYPES } from '../../brands/default/constants';
import { MAP_VIEW_DEAL } from '../../brands/default/content/components/map-view';
import { WISHLIST } from '../../brands/default/features';
import { trackEvent } from '../../helpers/analytics';
import { getDealApiPath } from '../../helpers/api';
import { formatCurrency } from '../../helpers/currency';
import {
  getIsChooseYourOptionsDeal,
  getIsChooseYourProductDeal,
  getUrlFromDeal,
  useDealImpresssion,
} from '../../helpers/deals';
import httpCommonHeaders from '../../helpers/httpCommonHeaders';
import { parseWowcherPath, redirectToURL } from '../../helpers/url';
import useRestoreScroll from '../../helpers/useRestoreScroll';
import ThemeContext from '../../providers/ThemeProvider';
import PrimaryButton from '../_generic/button/PrimaryButton';
import Clamp from '../_generic/utils/Clamp';
import EmptyDeal from '../deal/shared-components/DealEmpty';
import DealPriceSplat from '../deal/shared-components/DealPriceSplat';
import DealSocialCueTop from '../deal/shared-components/DealSocialCueTop';
import WrapperDealCard from '../deal/shared-components/WrapperDealCard';
import CarouselBlock from '../deal/shared-components/carousel/CarouselBlock';
import MapViewDealModal from './MapViewDealModal';

const WishlistIcon = dynamic(
  () => import('../deal/shared-components/WishlistIcon'),
  {
    ssr: false,
  },
);

const MapViewDeal = ({
  deal = null,
  socialcues = null,
  extraclasses = '',
  isCarousel,
}) => {
  const theme = useContext(ThemeContext);
  const router = useRouter();
  const pathUrl = parseWowcherPath(router.asPath);
  const location = useSelector((state) => state.locations.location.shortName);
  const componentRef = useRef(null);
  const [, setScrollData] = useRestoreScroll();
  const isHeadlineBig = deal?.headline?.length > 58;
  useDealImpresssion({ componentRef, dealId: deal?.id, location });
  const headLineAux = deal.headline || '';
  const isChooseYourOptions = getIsChooseYourOptionsDeal(deal);
  const isChooseYourProduct = getIsChooseYourProductDeal(deal);
  const discount = pathUrl?.details?.locationType !== DEAL_LOCATION_TYPES.local;
  const [isClicked, setIsClicked] = useState(false);
  const [mainDeal, setMainDeal] = useState();

  const dealUrlPath = getUrlFromDeal({
    deal,
    originPath: router.asPath,
  });
  const dealApiPath = getDealApiPath({
    currentPagePath: dealUrlPath,
    deal,
  });

  if (!deal || Object.keys(deal).length === 0) return <EmptyDeal />;

  const handleDealClick = async () => {
    setIsClicked(true);
    trackEvent('mv_deal_card_view');
    setScrollData({
      dealId: deal.id,
      page: deal?.page,
      url: window.location.href,
    });
    const response = await axios(dealApiPath, {
      headers: httpCommonHeaders(),
      method: 'GET',
      // widthCredentials is true if ssr is false
      withCredentials: true,
    });
    if (deal.productDisplay.type === 'calendar') {
      redirectToURL(dealUrlPath, router);
    } else {
      //  order products by price
      if (response?.data?.mainDeal?.products) {
        response.data.mainDeal.products.sort((a, b) =>
          a.price > b.price ? 1 : a.price < b.price ? -1 : 0,
        );
      }
      setMainDeal(response.data.mainDeal);
    }
  };

  return (
    <>
      {isClicked && mainDeal ? (
        <MapViewDealModal
          deal={mainDeal}
          handleBuyButtonClick={() => {}}
          isClicked={isClicked}
          setIsClicked={setIsClicked}
        />
      ) : null}
      <div
        className={`medium-deal ${extraclasses}`}
        id={`deal_${deal.id}`}
        ref={componentRef}
      >
        <WrapperDealCard>
          <div className="image-container">
            <DealSocialCueTop
              order={deal.order}
              socialCueClaim={deal?.socialCueClaim}
              socialCuesAvailability={deal.totalRemaining || 0}
              socialCuesBought={deal.totalBought || 0}
              socialCuesData={socialcues}
            />
            <CarouselBlock
              deal={deal}
              mapview
              onClick={handleDealClick}
              secondCheckout
              showImageOverlays
              showScrim
              single
            />
            <div
              className="deal-title"
              onClick={handleDealClick}
              onKeyDown={handleDealClick}
              role="link"
              tabIndex="0"
            >
              <Clamp lines={3}>
                <h3 className="headline">{headLineAux}</h3>
              </Clamp>
            </div>
            <DealPriceSplat deal={deal} discount={discount} />
            {WISHLIST && (
              <WishlistIcon
                dealId={deal.id}
                mapViewCarousel={isCarousel}
                mapViewLeft={!isCarousel}
              />
            )}
          </div>
          <a className="deal-info">
            {deal?.discount > 0 && deal?.display?.discount && (
              <p className="money-item">
                <span className="label">{MAP_VIEW_DEAL.was}</span>
                <span className="value-strike">
                  {formatCurrency(
                    deal?.currency,
                    deal?.originalPrice,
                    false,
                    true,
                  )}
                </span>
              </p>
            )}
            <p className="money-item">
              <span className="label">{deal.priceText}</span>
              <span className="value">
                {formatCurrency(deal?.currency, deal.price, false, true)}
              </span>
            </p>
            {!isChooseYourOptions && !isChooseYourProduct && (
              <PrimaryButton
                ariaLabel={MAP_VIEW_DEAL.view}
                onClick={handleDealClick}
              >
                {MAP_VIEW_DEAL.view}
              </PrimaryButton>
            )}
          </a>
        </WrapperDealCard>
      </div>

      <style jsx>{`
        :global(.medium-deal) {
          width: 300.33px;
          height: 260px;
          border-radius: 20px;
          box-shadow: 0 3px 10px rgb(0 0 0 / 20%);
          margin-bottom: 10px;
          margin-left: 5px;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          :global(.slick-current .medium-deal .card) {
            border: 3px solid ${theme.colors.primary};
          }
        }
        .image-container {
          position: relative;
          display: flex;
        }
        .deal-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-grow: 1;
          width: 100%;
          padding: 10px 10px;
          text-decoration: none;
        }
        .deal-title {
          flex-grow: 1;
          z-index: 1000;
          position: fixed;
          cursor: pointer;
          visibility: visible;
          bottom: 26%;
          color: white;
          max-width: 72%;
          padding: 1rem;
          margin: 0 0.8rem;
          border-radius: 6px;
          background-color: ${theme.colors.headingtext}36;
        }
        .headline {
          font-size: 14px;
          font-weight: bold;
          margin: 0;
          color: ${theme.colors.white};
        }
        .postage-text {
          margin-top: ${isHeadlineBig ? '-6px' : '0px'};
        }
        .medium-deal :global(.card) {
          width: 100%;
          height: 100%;
        }
        .medium-deal :global(.primary-button) {
          min-width: 136px;
          width: 96px;
          margin-right: 15px;
        }
        .money-item {
          line-height: 1em;
          margin-bottom: 0;
          min-width: 60px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-direction: column;
        }
        .label {
          font-size: 14px;
          line-height: 14px;
          text-transform: uppercase;
          color: black;
        }
        .value-strike {
          font-size: 16px;
          padding: 0 0.5rem;
          color: ${theme.colors.primary};
          background: linear-gradient(
            to left top,
            transparent 47.75%,
            currentColor 49.5%,
            currentColor 50.5%,
            transparent 52.25%
          );
        }
        .value {
          font-size: 16px;
          color: ${theme.colors.primary};
        }
        .money-item:last-child {
          margin-right: 0;
        }
        @media (max-width: ${theme.breakpoints.xlDown}) {
          :global(.medium-deal) {
            width: 300px;
            height: 260px;
            margin-bottom: 5px;
          }
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .medium-deal {
            width: 273px;
            height: 256px;
            border-radius: 20px;
          }
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.medium-deal) {
            border-radius: 10px;
            width: 95% !important;
            height: auto !important;
            margin-bottom: 10px;
            margin-right: 10px;
            margin-left: 16px;
          }
          .deal-info {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-grow: 1;
            width: 100%;
            padding: 0 10px;
            text-decoration: none;
            margin: 5px;
          }
          .deal-title {
            bottom: 20%;
          }
          .headline {
            color: ${theme.colors.dealmobiletitle};
            font-size: 16px;
            line-height: normal;
            font-weight: bold;
          }
          .medium-deal :global(.card) {
            width: 100%;
            height: auto;
            margin-bottom: 0;
            border-radius: 20px;
          }
          .medium-deal :global(.primary-button) {
            // display: none;
          }
          .medium-deal :global(.bought-count) {
            position: absolute;
            padding: 0;
            margin: 0;
            bottom: 10px;
            background-color: transparent;
            color: white;
          }
        }
      `}</style>
    </>
  );
};
MapViewDeal.propTypes = {
  deal: PropTypes.object,
  extraclasses: PropTypes.string,
  isCarousel: PropTypes.bool,
  socialcues: PropTypes.object,
};
export default MapViewDeal;
